import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

const STAGING_ROUTES = new Set(['/login', '/deposits', '/entry-periods']);
const STAGING_LABEL_COUNT = 40;

const labels = Array.from({ length: STAGING_LABEL_COUNT }, (_, index) => (
  <span className="staging-tape__label" key={index}>
    STAGING
  </span>
));

const StagingTape = () => {
  const location = useLocation();
  const isTapeActive = import.meta.env.VITE_STAGING === 'true' && STAGING_ROUTES.has(location.pathname);

  useEffect(() => {
    if (!isTapeActive) {
      return;
    }

    document.documentElement.classList.add('staging-taped');
    return () => document.documentElement.classList.remove('staging-taped');
  }, [isTapeActive]);

  if (!isTapeActive) {
    return null;
  }

  return createPortal(
    <>
      <div className="staging-tape" aria-hidden="true">
        <div className="staging-tape__strip staging-tape__strip--top">
          <div className="staging-tape__labels">{labels}</div>
        </div>
        <div className="staging-tape__strip staging-tape__strip--bottom">
          <div className="staging-tape__labels">{labels}</div>
        </div>
        <div className="staging-tape__strip staging-tape__strip--left">
          <div className="staging-tape__labels">{labels}</div>
        </div>
        <div className="staging-tape__strip staging-tape__strip--right">
          <div className="staging-tape__labels">{labels}</div>
        </div>
      </div>
      <style>{`
        html.staging-taped #root {
          position: fixed;
          inset: 32px;
          height: auto;
          overflow: auto;
          transform: translateZ(0);
        }

        html.staging-taped .h-screen {
          height: 100%;
        }

        html.staging-taped .min-h-screen {
          min-height: 100%;
        }

        .staging-tape,
        .staging-tape * {
          pointer-events: none;
          user-select: none;
        }

        .staging-tape__strip {
          position: fixed;
          z-index: 2147483000;
          display: flex;
          overflow: hidden;
          box-sizing: border-box;
          background: repeating-linear-gradient(45deg, #facc15 0 24px, #111 24px 48px);
          box-shadow: inset 0 0 0 1px rgb(0 0 0 / 18%), inset 0 -2px 0 rgb(0 0 0 / 20%);
          pointer-events: none;
        }

        .staging-tape__strip--top,
        .staging-tape__strip--bottom {
          right: 0;
          left: 0;
          height: 32px;
          align-items: center;
        }

        .staging-tape__strip--top {
          top: 0;
        }

        .staging-tape__strip--bottom {
          bottom: 0;
        }

        .staging-tape__strip--left,
        .staging-tape__strip--right {
          top: 0;
          bottom: 0;
          width: 32px;
          flex-direction: column;
          align-items: center;
        }

        .staging-tape__strip--left {
          left: 0;
        }

        .staging-tape__strip--right {
          right: 0;
        }

        .staging-tape__labels {
          display: flex;
          flex: none;
          gap: 12px;
        }

        .staging-tape__strip--left .staging-tape__labels,
        .staging-tape__strip--right .staging-tape__labels {
          flex-direction: column;
          gap: 10px;
        }

        .staging-tape__label {
          display: inline-flex;
          flex: none;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          padding: 4px 9px;
          background: #facc15;
          color: #111;
          font-family: ui-sans-serif, system-ui, sans-serif;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.16em;
          line-height: 1;
          white-space: nowrap;
        }

        .staging-tape__strip--left .staging-tape__label,
        .staging-tape__strip--right .staging-tape__label {
          width: 26px;
          min-height: 68px;
          padding: 7px 4px;
          writing-mode: vertical-rl;
        }

        .staging-tape__strip--left .staging-tape__label {
          transform: rotate(180deg);
        }
      `}</style>
    </>,
    document.body,
  );
};

export default StagingTape;
